import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from 'react-simple-maps';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const geoUrl = "https://raw.githubusercontent.com/subyfly/topojson/refs/heads/master/world-countries.json";

const DashboardMap = () => {
  const navigate = useNavigate();
  const [mapData, setMapData] = useState([]);
  const [hoveredLocation, setHoveredLocation] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [position, setPosition] = useState({ coordinates: [0, 20], zoom: 1 });

  const handleZoomIn = () => {
    if (position.zoom >= 4) return;
    setPosition(pos => ({ ...pos, zoom: pos.zoom * 1.5 }));
  };

  const handleZoomOut = () => {
    if (position.zoom <= 1) return;
    setPosition(pos => ({ ...pos, zoom: pos.zoom / 1.5 }));
  };

  const handleMoveEnd = useCallback((position) => {
    setPosition(position);
  }, []);

  const handleMarkerClick = (location) => {
    setSelectedLocation(location);
    setHoveredLocation(null);
  };

  const handleClosePopup = () => {
    setSelectedLocation(null);
  };

  useEffect(() => {
    const fetchMapData = async () => {
      try {
        const response = await api.get('/dashboard/map-annotations');
        setMapData(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching map data:', err);
        setError('Failed to load map data');
        setLoading(false);
      }
    };

    fetchMapData();

    const interval = setInterval(fetchMapData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-blue-500 rounded-full animate-pulse"></div>
          <div className="text-gray-500">Loading map data...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-red-500 flex items-center space-x-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full relative bg-gray-50">
      <ComposableMap
        projectionConfig={{
          scale: 150
        }}
        style={{
          width: '100%',
          height: '100%',
        }}
      >
        <ZoomableGroup
          zoom={position.zoom}
          center={position.coordinates}
          onMoveEnd={handleMoveEnd}
          maxZoom={4}
        >
          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map(geo => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill="#e5e7eb"
                  stroke="#9ca3af"
                  strokeWidth={0.3}
                  style={{
                    default: {
                      outline: 'none',
                      fillOpacity: 0.7
                    },
                    hover: {
                      outline: 'none',
                      fill: '#d1d5db',
                      fillOpacity: 0.8,
                      transition: 'all 250ms'
                    },
                    pressed: {
                      outline: 'none'
                    },
                  }}
                />
              ))
            }
          </Geographies>

          {mapData.map((location, index) => (
            <Marker
              key={`${location.host}-${index}`}
              coordinates={[parseFloat(location.longitude), parseFloat(location.latitude)]}
              onMouseEnter={() => !selectedLocation && setHoveredLocation(location)}
              onMouseLeave={() => !selectedLocation && setHoveredLocation(null)}
              onClick={() => handleMarkerClick(location)}
            >
              <circle
                r={5 / position.zoom}
                fill="#ef4444"
                stroke="#fff"
                strokeWidth={1.5 / position.zoom}
                className="cursor-pointer transition-all duration-200"
                style={{
                  filter: `drop-shadow(0 0 ${4 / position.zoom}px rgba(239,68,68,0.5))`
                }}
              />
            </Marker>
          ))}
        </ZoomableGroup>
      </ComposableMap>

      {/* Zoom Controls */}
      <div className="absolute left-4 top-4 flex flex-col gap-2">
        <button
          onClick={handleZoomIn}
          className="bg-white p-2 rounded-lg shadow-lg hover:bg-gray-50 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
        </button>
        <button
          onClick={handleZoomOut}
          className="bg-white p-2 rounded-lg shadow-lg hover:bg-gray-50 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
          </svg>
        </button>
      </div>

      {(hoveredLocation || selectedLocation) && (
        <div className="absolute p-3 bg-white rounded-lg shadow-lg text-sm border border-gray-200"
             style={{
               left: '50%',
               bottom: '20px',
               transform: 'translateX(-50%)',
               minWidth: '300px'
             }}>
          <div className="flex justify-between items-start mb-2">
            <code className="text-gray-900 font-mono">{(selectedLocation || hoveredLocation).host}</code>
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 text-xs font-medium text-white bg-blue-500 rounded">
                Confidence: {(selectedLocation || hoveredLocation).confidence}
              </span>
              {selectedLocation && (
                <button
                  onClick={handleClosePopup}
                  className="p-1 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-xs font-medium text-gray-500 uppercase">Location</div>
              <div className="text-gray-900">{(selectedLocation || hoveredLocation).country.city || 'Unknown City'}</div>
              <div className="text-gray-600">{(selectedLocation || hoveredLocation).country.country}</div>
            </div>
            <div>
              <div className="text-xs font-medium text-gray-500 uppercase">Timestamps</div>
              <div className="text-xs text-gray-600">
                First: {format(new Date((selectedLocation || hoveredLocation).firstSeen), 'yyyy-MM-dd HH:mm')}
              </div>
              <div className="text-xs text-gray-600">
                Last: {format(new Date((selectedLocation || hoveredLocation).lastSeen), 'yyyy-MM-dd HH:mm')}
              </div>
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <button
              onClick={() => navigate(`/search?q=${(selectedLocation || hoveredLocation).host}`)}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-blue-500 rounded hover:bg-blue-600 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Search Details
            </button>
          </div>
        </div>
      )}

      {/* Stats Overlay */}
      <div className="absolute top-4 right-4 bg-white p-3 rounded-lg shadow-lg border border-gray-200">
        <div className="text-sm font-medium text-gray-900 mb-2">Statistics</div>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-xs font-medium text-gray-500 uppercase">Total Locations</div>
            <div className="text-gray-900 font-semibold">{mapData.length}</div>
          </div>
          <div>
            <div className="text-xs font-medium text-gray-500 uppercase">Unique Countries</div>
            <div className="text-gray-900 font-semibold">
              {new Set(mapData.map(item => item.country.country)).size}
            </div>
          </div>
        </div>
        <div className="mt-2 text-xs text-gray-500">Last 24 hours</div>
      </div>
    </div>
  );
};

export default DashboardMap; 